/**
 * Theme-side guard for Fly Bundles products that are limited to one per cart.
 *
 * This file is loaded only on product pages where both of these product
 * metafields are true:
 *   - custom.is_bundle
 *   - custom.limit_bundle_to_one
 *
 * Fly Bundles owns the add-to-cart request. This guard does not patch fetch or
 * modify the app. It blocks repeat clicks around the app UI, then confirms the
 * parent bundle product in cart.js. Any unrecognized or failed state fails open.
 */
(function () {
  'use strict';

  var config = document.getElementById('zp-bundle-limit-guard');
  if (!config || config.dataset.initialized === 'true') return;
  config.dataset.initialized = 'true';

  var productId = String(config.dataset.productId || '');
  var cartUrl = String(config.dataset.cartUrl || '/cart.js');
  var limitMessage = String(
    config.dataset.limitMessage ||
      'This item is already in your cart. The maximum quantity is 1.'
  );
  var main = document.getElementById('MainContent');

  if (!productId || !main) return;

  var state = {
    checkingCart: true,
    pending: false,
    limitReached: false,
    verifyTimer: null,
    verifyAttempts: 0,
    refreshFrame: null,
  };
  var statusElement = null;

  var MAX_VERIFY_ATTEMPTS = 10;
  var VERIFY_INTERVAL_MS = 500;
  var FLY_ACTION_SELECTOR = [
    '[data-cart-btn-name]',
    '.gfbFrequentBoughtAddToCartButton',
    '.gfbAddToCartButton',
    '.gfbVolumeBundleAddToCartButton',
  ].join(',');

  /**
   * Add scoped styles only on the limited product page. Fly's live FBT action
   * is a div rather than a native button, so aria-disabled alone isn't enough.
   */
  function addGuardStyles() {
    var style = document.createElement('style');
    style.textContent =
      '[data-zp-bundle-limit-disabled="true"]{' +
      'pointer-events:none!important;opacity:.55!important;cursor:not-allowed!important;' +
      '}' +
      '[data-zp-bundle-limit-pending="true"]{opacity:.7!important;}' +
      '#zp-bundle-limit-status{margin:.5rem 0;}';
    document.head.appendChild(style);
  }

  /**
   * @param {EventTarget | null} target
   * @returns {Element | null}
   */
  function findFlyAction(target) {
    if (!(target instanceof Element)) return null;

    var action = target.closest(FLY_ACTION_SELECTOR);
    return action && main.contains(action) ? action : null;
  }

  /**
   * @returns {Element[]}
   */
  function getFlyActions() {
    return Array.prototype.slice.call(main.querySelectorAll(FLY_ACTION_SELECTOR));
  }

  /**
   * @returns {HTMLElement}
   */
  function getStatusElement() {
    if (statusElement) return statusElement;

    statusElement = document.createElement('p');
    statusElement.id = 'zp-bundle-limit-status';
    statusElement.setAttribute('role', 'status');
    statusElement.setAttribute('aria-live', 'polite');
    statusElement.hidden = true;
    return statusElement;
  }

  /**
   * Place the message outside Fly's React-owned subtree so an app re-render
   * doesn't delete it.
   *
   * @param {Element | null} action
   */
  function placeStatusElement(action) {
    var status = getStatusElement();
    if (status.isConnected || !action) return;

    var appBlock = action.closest('.shopify-block');
    var flyRoot = action.closest(
      '.gfbPageWrapper, .gfbFrequentBoughtWrapper, .gfbVolumeBundleWrapper, .gfbVolumeBlock'
    );
    var anchor = appBlock || flyRoot || action;
    anchor.insertAdjacentElement('afterend', status);
  }

  /**
   * @param {boolean} visible
   * @param {Element | null} [action]
   */
  function setStatus(visible, action) {
    var status = getStatusElement();
    placeStatusElement(action || getFlyActions()[0] || null);
    status.textContent = limitMessage;
    status.hidden = !visible;
  }

  /**
   * Only undo disabled/ARIA state that this guard added. Fly's own sold-out
   * state is never re-enabled.
   *
   * @param {Element} action
   */
  function unlockAction(action) {
    action.removeAttribute('aria-busy');
    action.removeAttribute('data-zp-bundle-limit-pending');

    if (action.getAttribute('data-zp-bundle-limit-disabled') !== 'true') return;

    if (
      action instanceof HTMLButtonElement ||
      action instanceof HTMLInputElement
    ) {
      action.disabled = false;
    }
    action.removeAttribute('aria-disabled');
    action.removeAttribute('data-zp-bundle-limit-disabled');
    action.removeAttribute('title');
  }

  /**
   * @param {Element} action
   */
  function lockAction(action) {
    action.removeAttribute('aria-busy');
    action.removeAttribute('data-zp-bundle-limit-pending');

    var isNativelyDisabled =
      (action instanceof HTMLButtonElement || action instanceof HTMLInputElement) && action.disabled;
    var isAriaDisabled = action.getAttribute('aria-disabled') === 'true';

    if (!isNativelyDisabled && !isAriaDisabled) {
      action.setAttribute('data-zp-bundle-limit-disabled', 'true');
      action.setAttribute('aria-disabled', 'true');
      action.setAttribute('title', limitMessage);

      if (
        action instanceof HTMLButtonElement ||
        action instanceof HTMLInputElement
      ) {
        action.disabled = true;
      }
    }
  }

  /**
   * Update late-rendered or replaced Fly buttons.
   */
  function refreshFlyActions() {
    state.refreshFrame = null;
    var actions = getFlyActions();

    for (var i = 0; i < actions.length; i++) {
      var action = actions[i];

      if (state.limitReached) {
        lockAction(action);
      } else {
        unlockAction(action);
        if (state.pending || state.checkingCart) {
          action.setAttribute('aria-busy', 'true');
          action.setAttribute('data-zp-bundle-limit-pending', 'true');
        }
      }
    }

    if (state.limitReached) setStatus(true, actions[0] || null);
  }

  function scheduleRefresh() {
    if (state.refreshFrame !== null) return;
    state.refreshFrame = window.requestAnimationFrame(refreshFlyActions);
  }

  /**
   * @param {unknown} cart
   * @returns {number | null}
   */
  function getProductQuantity(cart) {
    if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items)) return null;

    var quantity = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (String(item.product_id || '') === productId) {
        quantity += Number(item.quantity) || 0;
      }
    }

    return quantity;
  }

  /**
   * @param {unknown} cart
   * @returns {boolean} Whether a recognizable cart was applied.
   */
  function applyCart(cart) {
    var quantity = getProductQuantity(cart);
    if (quantity === null) return false;

    state.limitReached = quantity >= 1;
    state.checkingCart = false;
    state.pending = false;
    state.verifyAttempts = 0;

    if (state.verifyTimer !== null) {
      window.clearTimeout(state.verifyTimer);
      state.verifyTimer = null;
    }

    if (!state.limitReached) setStatus(false);
    scheduleRefresh();
    return true;
  }

  /**
   * @returns {Promise<unknown>}
   */
  function fetchCart() {
    return fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to read cart');
      return response.json();
    });
  }

  /**
   * Confirm Fly's asynchronous add without intercepting its request.
   */
  function verifyCartAfterAdd() {
    if (!state.pending) return;

    state.verifyAttempts += 1;
    fetchCart()
      .then(function (cart) {
        var quantity = getProductQuantity(cart);
        if (quantity !== null && quantity >= 1) {
          applyCart(cart);
          return;
        }

        if (state.verifyAttempts >= MAX_VERIFY_ATTEMPTS) {
          state.pending = false;
          state.verifyAttempts = 0;
          setStatus(false);
          scheduleRefresh();
          return;
        }

        state.verifyTimer = window.setTimeout(verifyCartAfterAdd, VERIFY_INTERVAL_MS);
      })
      .catch(function () {
        // A temporary cart read failure must not permanently block purchasing.
        if (state.verifyAttempts >= MAX_VERIFY_ATTEMPTS) {
          state.pending = false;
          state.verifyAttempts = 0;
          setStatus(false);
          scheduleRefresh();
          return;
        }

        state.verifyTimer = window.setTimeout(verifyCartAfterAdd, VERIFY_INTERVAL_MS);
      });
  }

  /**
   * Capture before Fly's normal target/bubble handler. The first click passes
   * through; subsequent clicks are blocked while pending or after confirmation.
   *
   * @param {MouseEvent} event
   */
  function onMainClick(event) {
    var action = findFlyAction(event.target);
    if (!action) return;

    // Close the small load-time race where an existing cart could otherwise
    // receive a second bundle before the initial cart.js request resolves.
    if (state.checkingCart) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (state.limitReached || state.pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setStatus(true, action);
      return;
    }

    state.pending = true;
    state.verifyAttempts = 0;
    action.setAttribute('aria-busy', 'true');
    action.setAttribute('data-zp-bundle-limit-pending', 'true');
    state.verifyTimer = window.setTimeout(verifyCartAfterAdd, VERIFY_INTERVAL_MS);
  }

  /**
   * @param {Event} event
   */
  function onThemeCartUpdate(event) {
    var detail = event.detail || {};
    var resource = detail.resource;
    var quantity = getProductQuantity(resource);
    var isFullCart =
      resource &&
      typeof resource === 'object' &&
      ('token' in resource || 'item_count' in resource || 'total_price' in resource);

    if (quantity !== null && (quantity >= 1 || isFullCart) && applyCart(resource)) return;
    fetchCart().then(applyCart).catch(function () {});
  }

  /**
   * Shopify's standard storefront event exposes a promise that resolves with
   * the updated cart. Fly may adopt it; the fallback verifier remains in place.
   *
   * @param {Event} event
   */
  function onStandardCartUpdate(event) {
    var promise = event.promise || (event.detail && event.detail.promise);

    if (promise && typeof promise.then === 'function') {
      promise
        .then(function (result) {
          if (!applyCart(result && result.cart)) {
            return fetchCart().then(applyCart);
          }
        })
        .catch(function () {
          if (state.pending) {
            state.pending = false;
            scheduleRefresh();
          }
        });
      return;
    }

    fetchCart().then(applyCart).catch(function () {});
  }

  function onCartError() {
    if (!state.pending) return;
    state.pending = false;
    state.verifyAttempts = 0;
    if (state.verifyTimer !== null) {
      window.clearTimeout(state.verifyTimer);
      state.verifyTimer = null;
    }
    setStatus(false);
    scheduleRefresh();
  }

  main.addEventListener('click', onMainClick, true);
  document.addEventListener('cart:update', onThemeCartUpdate);
  document.addEventListener('cart:error', onCartError);
  document.addEventListener('shopify:cart:lines-update', onStandardCartUpdate);
  window.addEventListener('pageshow', function () {
    state.checkingCart = true;
    scheduleRefresh();
    fetchCart().then(applyCart).catch(function () {
      state.checkingCart = false;
      scheduleRefresh();
    });
  });

  var observer = new MutationObserver(scheduleRefresh);
  observer.observe(main, { childList: true, subtree: true });

  addGuardStyles();

  // Fail open when the initial cart request cannot be read.
  fetchCart().then(applyCart).catch(function () {
    state.checkingCart = false;
    scheduleRefresh();
  });
})();
